/**
 * PortalVerticals — Admin page for managing verticals
 *
 * CRUD interface for the `verticals` table. Uses raw fetch() against
 * the Supabase REST API (the JS client hangs on production).
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Target, Plus, Trash2, Loader2 } from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────────

interface Vertical {
  id: string;
  name: string;
  cluster: string;
  payout_score: number;
  conversion_score: number;
  cross_sell_potential: number;
  compliance_risk: number;
  requires: { field: string; operator: string; value: string }[];
  call_capable: boolean;
  route_type: 'lead' | 'call' | 'redirect';
  route_url: string | null;
  route_meta: Record<string, unknown> | null;
  is_active: boolean;
}

type EditingCell = {
  id: string;
  field: 'payout_score' | 'conversion_score' | 'compliance_risk' | 'route_url';
} | null;

// ── Supabase helpers ───────────────────────────────────────────────────

const SUPABASE_URL =
  (import.meta as any).env?.VITE_SUPABASE_URL ||
  'https://khwvlnkrtrwvfbnnjsrc.supabase.co';
const SUPABASE_KEY =
  (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || '';

function getHeaders() {
  const raw = localStorage.getItem('supabase.auth.token') || '{}';
  const token = JSON.parse(raw);
  return {
    apikey: SUPABASE_KEY,
    Authorization: `Bearer ${token.access_token}`,
    'Content-Type': 'application/json',
    Prefer: 'return=representation',
  };
}

// ── Cluster colour map ─────────────────────────────────────────────────

const CLUSTER_COLORS: Record<string, { bg: string; text: string; ring: string }> = {
  insurance:      { bg: 'bg-emerald-500/15', text: 'text-emerald-400', ring: 'ring-emerald-500/30' },
  debt_credit:    { bg: 'bg-violet-500/15',  text: 'text-violet-400',  ring: 'ring-violet-500/30' },
  finance:        { bg: 'bg-blue-500/15',    text: 'text-blue-400',    ring: 'ring-blue-500/30' },
  home_services:  { bg: 'bg-amber-500/15',   text: 'text-amber-400',   ring: 'ring-amber-500/30' },
  legal:          { bg: 'bg-rose-500/15',    text: 'text-rose-400',    ring: 'ring-rose-500/30' },
  health:         { bg: 'bg-cyan-500/15',    text: 'text-cyan-400',    ring: 'ring-cyan-500/30' },
  catch_all:      { bg: 'bg-slate-500/15',   text: 'text-slate-400',   ring: 'ring-slate-500/30' },
};

function ClusterPill({ cluster }: { cluster: string }) {
  const c = CLUSTER_COLORS[cluster] || CLUSTER_COLORS.catch_all;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${c.bg} ${c.text} ${c.ring}`}
    >
      {cluster.replace('_', ' ')}
    </span>
  );
}

// ── Blank vertical template ────────────────────────────────────────────

const BLANK_VERTICAL: Omit<Vertical, 'id'> & { id: string } = {
  id: '',
  name: '',
  cluster: 'catch_all',
  payout_score: 0,
  conversion_score: 0,
  cross_sell_potential: 0,
  compliance_risk: 0,
  requires: [],
  call_capable: false,
  route_type: 'lead',
  route_url: '',
  route_meta: null,
  is_active: true,
};

// ── Component ──────────────────────────────────────────────────────────

export default function PortalVerticals() {
  const [verticals, setVerticals] = useState<Vertical[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditingCell>(null);
  const [editValue, setEditValue] = useState('');

  // Add-vertical modal
  const [showAdd, setShowAdd] = useState(false);
  const [newVertical, setNewVertical] = useState({ ...BLANK_VERTICAL });
  const [addLoading, setAddLoading] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Vertical | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────

  const fetchVerticals = useCallback(async () => {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/verticals?select=*&order=name.asc`,
        { headers: getHeaders() },
      );
      if (!res.ok) throw new Error(await res.text());
      const data: Vertical[] = await res.json();
      setVerticals(data);
    } catch (err) {
      console.error('Failed to fetch verticals:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVerticals();
  }, [fetchVerticals]);

  // ── Patch a single field ───────────────────────────────────────────

  const patchField = useCallback(
    async (id: string, patch: Partial<Vertical>) => {
      setSaving(id);
      try {
        const res = await fetch(
          `${SUPABASE_URL}/rest/v1/verticals?id=eq.${id}`,
          {
            method: 'PATCH',
            headers: getHeaders(),
            body: JSON.stringify(patch),
          },
        );
        if (!res.ok) throw new Error(await res.text());
        const updated: Vertical[] = await res.json();
        if (updated.length) {
          setVerticals((prev) =>
            prev.map((v) => (v.id === id ? updated[0] : v)),
          );
        }
      } catch (err) {
        console.error('Patch failed:', err);
      } finally {
        setSaving(null);
      }
    },
    [],
  );

  // ── Inline-edit commit ─────────────────────────────────────────────

  const commitEdit = useCallback(() => {
    if (!editing) return;
    const { id, field } = editing;
    const v = verticals.find((x) => x.id === id);
    if (!v) return;

    if (field === 'route_url') {
      if (editValue !== (v.route_url || '')) {
        patchField(id, { route_url: editValue || null });
      }
    } else {
      const num = Number(editValue);
      if (!Number.isNaN(num) && num !== v[field]) {
        patchField(id, { [field]: num });
      }
    }
    setEditing(null);
  }, [editing, editValue, verticals, patchField]);

  // ── Add vertical ──────────────────────────────────────────────────

  const handleAdd = useCallback(async () => {
    if (!newVertical.id || !newVertical.name) return;
    setAddLoading(true);
    try {
      const body = {
        ...newVertical,
        route_url: newVertical.route_url || null,
        requires: newVertical.requires,
        route_meta: newVertical.route_meta,
      };
      const res = await fetch(`${SUPABASE_URL}/rest/v1/verticals`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      const created: Vertical[] = await res.json();
      if (created.length) {
        setVerticals((prev) => [...prev, created[0]].sort((a, b) => a.name.localeCompare(b.name)));
      }
      setShowAdd(false);
      setNewVertical({ ...BLANK_VERTICAL });
    } catch (err) {
      console.error('Create failed:', err);
    } finally {
      setAddLoading(false);
    }
  }, [newVertical]);

  // ── Delete vertical ───────────────────────────────────────────────

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/verticals?id=eq.${deleteTarget.id}`,
        { method: 'DELETE', headers: getHeaders() },
      );
      if (!res.ok) throw new Error(await res.text());
      setVerticals((prev) => prev.filter((v) => v.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget]);

  // ── Render helpers ─────────────────────────────────────────────────

  function renderNumberCell(v: Vertical, field: 'payout_score' | 'conversion_score' | 'compliance_risk') {
    const isEditing = editing?.id === v.id && editing?.field === field;
    if (isEditing) {
      return (
        <Input
          type="number"
          autoFocus
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitEdit();
            if (e.key === 'Escape') setEditing(null);
          }}
          className="h-7 w-16 bg-slate-800 border-slate-700 text-white text-center text-sm"
        />
      );
    }
    return (
      <button
        type="button"
        className="px-2 py-0.5 rounded hover:bg-slate-800 text-sm tabular-nums cursor-pointer transition-colors"
        onClick={() => {
          setEditing({ id: v.id, field });
          setEditValue(String(v[field]));
        }}
      >
        {v[field]}
      </button>
    );
  }

  function renderRouteUrlCell(v: Vertical) {
    const isEditing = editing?.id === v.id && editing?.field === 'route_url';
    if (isEditing) {
      return (
        <Input
          autoFocus
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitEdit();
            if (e.key === 'Escape') setEditing(null);
          }}
          className="h-7 w-48 bg-slate-800 border-slate-700 text-white text-sm"
          placeholder="https://..."
        />
      );
    }
    return (
      <button
        type="button"
        className="max-w-[200px] truncate px-2 py-0.5 rounded hover:bg-slate-800 text-sm text-slate-400 cursor-pointer transition-colors"
        onClick={() => {
          setEditing({ id: v.id, field: 'route_url' });
          setEditValue(v.route_url || '');
        }}
      >
        {v.route_url || '—'}
      </button>
    );
  }

  // ── Page ───────────────────────────────────────────────────────────

  return (
    <PortalLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/10 rounded-lg">
              <Target className="h-6 w-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Verticals</h1>
              <p className="text-sm text-slate-400">
                Manage routing verticals, scoring, and activation
              </p>
            </div>
          </div>
          <Button
            onClick={() => setShowAdd(true)}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Vertical
          </Button>
        </div>

        {/* Table card */}
        <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
            </div>
          ) : verticals.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              No verticals found. Click "Add Vertical" to create one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Cluster</th>
                    <th className="px-4 py-3 font-medium text-center">Payout</th>
                    <th className="px-4 py-3 font-medium text-center">Conversion</th>
                    <th className="px-4 py-3 font-medium text-center">Risk</th>
                    <th className="px-4 py-3 font-medium text-center">Call?</th>
                    <th className="px-4 py-3 font-medium">Route</th>
                    <th className="px-4 py-3 font-medium text-center">Active</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {verticals.map((v) => (
                    <tr
                      key={v.id}
                      className={`hover:bg-slate-800/40 transition-colors ${
                        saving === v.id ? 'opacity-60' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <span className="font-medium text-white text-sm">{v.name}</span>
                        <span className="ml-2 text-[10px] text-slate-600 font-mono">{v.id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <ClusterPill cluster={v.cluster} />
                      </td>
                      <td className="px-4 py-3 text-center">{renderNumberCell(v, 'payout_score')}</td>
                      <td className="px-4 py-3 text-center">{renderNumberCell(v, 'conversion_score')}</td>
                      <td className="px-4 py-3 text-center">{renderNumberCell(v, 'compliance_risk')}</td>
                      <td className="px-4 py-3 text-center">
                        <Switch
                          checked={v.call_capable}
                          onCheckedChange={(checked) =>
                            patchField(v.id, { call_capable: checked })
                          }
                          className="data-[state=checked]:bg-emerald-600"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase font-mono text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">
                            {v.route_type}
                          </span>
                          {renderRouteUrlCell(v)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch
                          checked={v.is_active}
                          onCheckedChange={(checked) =>
                            patchField(v.id, { is_active: checked })
                          }
                          className="data-[state=checked]:bg-emerald-600"
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-500 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                          onClick={() => setDeleteTarget(v)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Add Vertical Dialog ──────────────────────────────────────── */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Vertical</DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new routing vertical with scoring and route config.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            {/* ID + Name */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300">ID (slug)</Label>
                <Input
                  value={newVertical.id}
                  onChange={(e) =>
                    setNewVertical((p) => ({ ...p, id: e.target.value }))
                  }
                  placeholder="e.g. auto_insurance"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Name</Label>
                <Input
                  value={newVertical.name}
                  onChange={(e) =>
                    setNewVertical((p) => ({ ...p, name: e.target.value }))
                  }
                  placeholder="Auto Insurance"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            {/* Cluster */}
            <div className="space-y-1.5">
              <Label className="text-slate-300">Cluster</Label>
              <select
                value={newVertical.cluster}
                onChange={(e) =>
                  setNewVertical((p) => ({ ...p, cluster: e.target.value }))
                }
                className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {Object.keys(CLUSTER_COLORS).map((c) => (
                  <option key={c} value={c}>
                    {c.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>

            {/* Scores row */}
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300">Payout (0-20)</Label>
                <Input
                  type="number"
                  min={0}
                  max={20}
                  value={newVertical.payout_score}
                  onChange={(e) =>
                    setNewVertical((p) => ({
                      ...p,
                      payout_score: Number(e.target.value),
                    }))
                  }
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Conv. (0-15)</Label>
                <Input
                  type="number"
                  min={0}
                  max={15}
                  value={newVertical.conversion_score}
                  onChange={(e) =>
                    setNewVertical((p) => ({
                      ...p,
                      conversion_score: Number(e.target.value),
                    }))
                  }
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Cross-Sell (0-10)</Label>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={newVertical.cross_sell_potential}
                  onChange={(e) =>
                    setNewVertical((p) => ({
                      ...p,
                      cross_sell_potential: Number(e.target.value),
                    }))
                  }
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Risk (0-15)</Label>
                <Input
                  type="number"
                  min={0}
                  max={15}
                  value={newVertical.compliance_risk}
                  onChange={(e) =>
                    setNewVertical((p) => ({
                      ...p,
                      compliance_risk: Number(e.target.value),
                    }))
                  }
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            {/* Route type + URL */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300">Route Type</Label>
                <select
                  value={newVertical.route_type}
                  onChange={(e) =>
                    setNewVertical((p) => ({
                      ...p,
                      route_type: e.target.value as Vertical['route_type'],
                    }))
                  }
                  className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="lead">lead</option>
                  <option value="call">call</option>
                  <option value="redirect">redirect</option>
                </select>
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="text-slate-300">Route URL</Label>
                <Input
                  value={newVertical.route_url || ''}
                  onChange={(e) =>
                    setNewVertical((p) => ({ ...p, route_url: e.target.value }))
                  }
                  placeholder="https://..."
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            {/* Toggles */}
            <div className="flex items-center gap-6 pt-1">
              <div className="flex items-center gap-2">
                <Switch
                  checked={newVertical.call_capable}
                  onCheckedChange={(checked) =>
                    setNewVertical((p) => ({ ...p, call_capable: checked }))
                  }
                  className="data-[state=checked]:bg-emerald-600"
                />
                <Label className="text-slate-300">Call Capable</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={newVertical.is_active}
                  onCheckedChange={(checked) =>
                    setNewVertical((p) => ({ ...p, is_active: checked }))
                  }
                  className="data-[state=checked]:bg-emerald-600"
                />
                <Label className="text-slate-300">Active</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowAdd(false)}
              className="text-slate-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={addLoading || !newVertical.id || !newVertical.name}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {addLoading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Create Vertical
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation Dialog ───────────────────────────────── */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <DialogContent className="bg-slate-900 border-slate-800 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Vertical</DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-white">
                {deleteTarget?.name}
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              className="text-slate-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              {deleting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}
